// The help of the page a person is on (18-help-and-training.md §18.1): which walkthrough it has, the
// steps this person is shown, the walkthrough or demo when it runs, and the first-visit nudge. Each
// app wires it to its router, its permissions and where it keeps records (the `recordWalkthrough`
// command for staff, a device preference on the partner link).
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  shouldOfferWalkthrough,
  visibleSteps,
  walkthroughMinutes,
  type TrainingRecords,
  type Walkthrough,
  type WalkthroughMode,
  type WalkthroughOutcome,
  type WalkthroughStep,
} from '@ecobox/domain/walkthroughs';
import type { Permission } from '@ecobox/domain';
import { Tour, type TourEnd, type TourLabels } from './Tour';
import { WalkthroughNudge, type WalkthroughNudgeLabels } from './HelpMenu';

export interface WalkthroughTakenEvent {
  walkthroughId: string;
  version: number;
  outcome: WalkthroughOutcome;
  mode: WalkthroughMode;
}

export interface PageHelpOptions {
  /** The walkthrough of the page the person is on, or null when it has none they may see. */
  walkthrough: Walkthrough | null;
  /** The person's permissions: steps with `anyOf` are shown only to those who hold one. */
  held: ReadonlySet<Permission>;
  /** The person's records, or undefined while they load (no nudge until they have). */
  records: TrainingRecords | undefined;
  /** Keep the record: called once for every walkthrough that ends, and for "Not now". */
  onRecord: (taken: WalkthroughTakenEvent) => void;
  /** Offer the first-visit nudge on this page. Default true. */
  nudge?: boolean | undefined;
  tourLabels?: Partial<TourLabels> | undefined;
  nudgeLabels?: Partial<WalkthroughNudgeLabels> | undefined;
}

export interface PageHelp {
  walkthrough: Walkthrough | null;
  /** The steps this person is shown. Empty when the page has no walkthrough. */
  steps: readonly WalkthroughStep[];
  /** The mode of the walkthrough that is running, or null. */
  running: WalkthroughMode | null;
  start: (mode: WalkthroughMode) => void;
  /** Give this to the Help button, so focus returns to it when a walkthrough ends. */
  buttonRef: RefObject<HTMLButtonElement | null>;
  nudgeShown: boolean;
  /** The running walkthrough (it portals to the body): render it once, anywhere. */
  overlay: ReactNode;
  /**
   * The first-visit nudge, or null. Render it in the flow of the page, above the page's content: it
   * takes its own room and never covers anything.
   */
  nudge: ReactNode;
}

interface Running {
  walkthrough: Walkthrough;
  mode: WalkthroughMode;
  steps: readonly WalkthroughStep[];
}

const key = (w: Walkthrough) => `${w.id}@${w.version}`;

export function usePageHelp({
  walkthrough,
  held,
  records,
  onRecord,
  nudge = true,
  tourLabels,
  nudgeLabels,
}: PageHelpOptions): PageHelp {
  const [running, setRunning] = useState<Running | null>(null);
  // Walkthroughs offered (or taken) in this session. The record reaches the server later when the
  // device is offline, so this keeps the nudge from coming back in the meantime. Memory only.
  const [handled, setHandled] = useState<ReadonlySet<string>>(() => new Set());
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const record = useRef(onRecord);
  useEffect(() => {
    record.current = onRecord;
  });

  const steps = useMemo(
    () => (walkthrough ? visibleSteps(walkthrough, held) : []),
    [walkthrough, held],
  );

  const markHandled = useCallback((w: Walkthrough) => {
    setHandled((cur) => (cur.has(key(w)) ? cur : new Set([...cur, key(w)])));
  }, []);

  const start = useCallback(
    (mode: WalkthroughMode) => {
      if (!walkthrough || steps.length === 0) return;
      markHandled(walkthrough);
      setRunning({ walkthrough, mode, steps });
    },
    [walkthrough, steps, markHandled],
  );

  // Leaving the page while its walkthrough runs closes it early (adjusted during render, recorded
  // in the effect below).
  const [left, setLeft] = useState<Running | null>(null);
  if (running && running.walkthrough.id !== walkthrough?.id) {
    setLeft(running);
    setRunning(null);
  }
  useEffect(() => {
    if (!left) return;
    const w = left.walkthrough;
    record.current({
      walkthroughId: w.id,
      version: w.version,
      outcome: 'skipped',
      mode: left.mode,
    });
  }, [left]);

  const onEnd = useCallback(
    (end: TourEnd) => {
      if (!running) return;
      const w = running.walkthrough;
      setRunning(null);
      record.current({
        walkthroughId: w.id,
        version: w.version,
        outcome: end.outcome,
        mode: end.mode,
      });
    },
    [running],
  );

  const nudgeShown =
    nudge &&
    running === null &&
    walkthrough !== null &&
    steps.length > 0 &&
    records !== undefined &&
    !handled.has(key(walkthrough)) &&
    shouldOfferWalkthrough(walkthrough, records[walkthrough.id]);

  const dismiss = useCallback(() => {
    if (!walkthrough) return;
    markHandled(walkthrough);
    record.current({
      walkthroughId: walkthrough.id,
      version: walkthrough.version,
      outcome: 'skipped',
      mode: 'guided',
    });
  }, [walkthrough, markHandled]);

  const overlay = running ? (
    <Tour
      key={`${running.walkthrough.id}-${running.mode}`}
      title={running.walkthrough.pageTitle}
      steps={running.steps}
      mode={running.mode}
      onEnd={onEnd}
      labels={tourLabels}
      returnFocusTo={buttonRef}
    />
  ) : null;
  const nudgeCard = nudgeShown ? (
    <WalkthroughNudge
      minutes={walkthroughMinutes(steps)}
      onStart={() => start('guided')}
      onDismiss={dismiss}
      labels={nudgeLabels}
    />
  ) : null;

  return {
    walkthrough,
    steps,
    running: running?.mode ?? null,
    start,
    buttonRef,
    nudgeShown,
    overlay,
    nudge: nudgeCard,
  };
}
